import {
  Button, Typography, useTheme,
} from '@mui/material';
import { Box } from '@mui/system';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ContentSearchView from './ContentSearchView';
import { useIsMobile } from '../../../common/hooks/useResponsive';
import { TestIds } from '../../../assets/testIds';
import { useSafeBack } from '../../../common/hooks/useSafeBack';
import { HOME_ROUTE } from '../../../routes/namedRoutes';

interface ContentViewMoreHeaderProps {
  showBackBtn?: boolean;
  showSearchBar?: boolean;
}

export default function ContentViewMoreHeader({
  showBackBtn = true,
  showSearchBar = true,
}: ContentViewMoreHeaderProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const safeBack = useSafeBack(HOME_ROUTE);

  const handleSubmit = (value: string | undefined) => {
    const params = new URLSearchParams();
    const trimmedValue = value?.trim();
    if (!trimmedValue) return;

    params.set('query', trimmedValue);
    const route = `/search?${params.toString()}`;
    navigate(route);
  };

  const SearchBar = (
    <Box
      id='content-search-bar'
      sx={{
        display: 'flex',
        flex: 1,
      }}
    >
      <ContentSearchView onSubmit={handleSubmit} />
    </Box>
  );

  return (
    <Box
      id='content-view-more-page-header'
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        padding: `${isMobile ? theme.spacing(1) : theme.spacing(0)} 0px ${theme.spacing(3)} 0px`,
        width: '100%',
      }}
    >
      {showBackBtn &&
      <Button
        data-testid={TestIds.contentArticleView.backBtn}
        sx={{
          color: theme.palette.grey[700],
          padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
          gap: theme.spacing(0.5),
          minWidth: theme.spacing(8),
          height: theme.spacing(6),
          flexShrink: 0,
          '&:hover': {
            backgroundColor: theme.palette.common.white,
          },
          borderRadius: theme.spacing(1),
          border: `1px solid ${theme.palette.grey[300]}`,
          boxShadow: '0px 0px 0px 1px rgba(10, 13, 18, 0.18) inset, 0px -2px 0px 0px rgba(10, 13, 18, 0.05) inset, 0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
        }}
        onClick={safeBack}
      >
        <ArrowBackIcon sx={{
          width: theme.spacing(2.5),
          height: theme.spacing(2.5),
        }} />
        <Typography variant='h6'>{t('button.back')}</Typography>
      </Button>
      }
      {showSearchBar && SearchBar}
    </Box>
  );
}
